import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { makeStyles } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Done';
import Avatar from '@material-ui/core/Avatar';
import ButtonBase from '@material-ui/core/ButtonBase';

// Data
import { useDispatch } from 'react-redux';
import { negatorFromType } from 'data/scheduledTransactions/scheduledTransactionsUtils';
import { updateScheduledTransaction, deleteScheduledTransaction } from 'data/scheduledTransactions/scheduledTransactionsActions';
import { mkScheduledTransaction } from 'data/scheduledTransactions/scheduledTransactionsTypes';
import { CashFlowTransaction } from 'data/transactions/types';

// CUSTOM
import AmountSpan from 'components/QuickenControls/AmountField/AmountSpan';
import PayeeSpan from 'components/QuickenControls/PayeeField/PayeeSpan';
import CategoryField from 'components/QuickenControls/CategoryField';
import tracker from 'utils/tracker';
import RecurrenceSelect from './RecurrenceSelect';


const useStyles = makeStyles((theme) => ({
  root: {
    height: 80,
    borderRadius: 8,
    margin: 8,
    width: 'calc(50% - 16px)',
    overflow: 'hidden',
    border: `2px solid ${theme.palette.greyScale[5]}`,
    padding: '14px 16px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.palette.greyScale[7],
    '&:hover': {
      border: `2px solid ${theme.palette.greyScale[4]}`,
      boxShadow: `0 1px 4px 0 ${theme.applyOpacityToHex(theme.palette.greyScale[0], 0.12)}`,
    },
  },
  slash: ({isBill}) => ({
    position: 'absolute',
    left: -8,
    height: 92,
    width: 44,
    backgroundColor: theme.applyOpacityToHex(isBill ? theme.palette.secondary.main : theme.palette.others.positive, 0.2),
    transform: 'rotate(-12deg)',
  }),
  avatar: ({isBill}) => ({
    height: 32,
    width: 32,
    borderRadius: 4,
    backgroundColor: isBill ? theme.palette.secondary.main : theme.palette.others.positive,
    fontWeight: 600,
  }),

  verticalStack: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: 252,
    padding: '0 10px',
  },
  row: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },

  payee: {
    flexGrow: 1,
    height: 28,
    fontWeight: '500 !important',
    lineHeight: '26px !important',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  amount: {
    flexShrink: 0,
    height: 28,
    fontWeight: '500 !important',
  },
  grey: {
    color: `${theme.palette.greyScale[2]} !important`,
  },
  category: {
    width: '130px !important',
    paddingLeft: 6,
    borderRadius: 3,
    '& button': {
      opacity: 0,
      '&:hover': {
        opacity: 1,
      },
    },
    '& .Mui-focused': {
      '& button': {
        opacity: 1,
      },
    },
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.applyOpacityToHex(theme.palette.greyScale[0], 0.1),
    },
  },

  toggleButton: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    '&:hover': {
      boxShadow: `0 1px 6px 1px ${theme.applyOpacityToHex(theme.palette.greyScale[0], 0.3)}`,
    },
  },

  greenCheck: {
    color: theme.palette.greyScale[7],
    backgroundColor: theme.palette.color9.opacity100,
    borderRadius: '50%',
    padding: 2,
  },
  uncheck: {
    color: theme.palette.greyScale[7],
    backgroundColor: theme.palette.greyScale[7],
    borderRadius: '50%',
    border: `2px solid ${theme.palette.greyScale[3]}`,
  },

  faded: {
    backgroundColor: `${theme.applyOpacityToHex(theme.palette.greyScale[3], 0.2)} !important`,
  },
  disabled: {
    backgroundColor: `${theme.palette.greyScale[3]} !important`,
  },
}));



export default function RecurringChip(props) {
  const { model, index, isBill } = props;
  const classes = useStyles({isBill});
  const transaction = model.transaction || {};
  const dispatch = useDispatch();

  const [verified, setKeep] = useState(true);
  const [mounted, setMounted] = useState(false);
  // Scheduled Txn Values:
  const negator = negatorFromType(model.type);

  // state
  const [payee, setPayee] = useState(transaction && transaction.payee);
  const [amount, setAmount] = useState(transaction && transaction.amount);
  const [category, setCategory] = useState(transaction && transaction.coa);
  const [recurrence, setRecurrence] = useState(model && model.recurrence);
  // refs for latest value capture when update/delete on unmount
  const capture = { payee, amount, category, recurrence, verified };
  const latestCapture = useRef();
  latestCapture.current = capture;

  useEffect(() => { // update state values on model change
    setPayee(transaction && transaction.payee);
    setAmount(transaction && transaction.amount);
    setCategory(transaction && transaction.coa);
    setRecurrence(model && model.recurrence);
  }, [model]); // eslint-disable-line react-hooks/exhaustive-deps

  // process verification on unmount
  useEffect(() => {
    setTimeout(() => setMounted(true), 0);
    return () => {
      const data = latestCapture.current;
      const spreadTxn = transaction.toJS ? transaction.toJS() : transaction;
      dispatch(data.verified ?
        updateScheduledTransaction(mkScheduledTransaction({
          ...model.toJS(),
          recurrence: data.recurrence,
          transaction: new CashFlowTransaction({
            ...spreadTxn,
            payee: data.payee,
            amount: data.amount,
            coa: data.category,
          }),
          isUserVerified: true,
        }))
        :
        deleteScheduledTransaction(mkScheduledTransaction({
          ...model.toJS(),
          isUserVerified: true,
        })));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleVerify = () => {
    props.reportFunc(model.id, !verified);
    setKeep(!verified);
  };

  // mix-panel tracking on field blur
  const updatePayee = (val) => {
    if (payee !== val) {
      setPayee(val);
      tracker.track(tracker.events.editModel, { field: 'payee' });
    }
  };
  const updateAmount = (val) => {
    if (amount !== val) {
      setAmount(val);
      tracker.track(tracker.events.editModel, { field: 'amount' });
    }
  };
  const updateCategory = (val) => {
    if (category !== val) {
      setCategory(val);
      tracker.track(tracker.events.editModel, { field: 'category' });
    }
  };
  const updateRecurrence = (val) => {
    if (recurrence !== val) {
      setRecurrence(val);
      tracker.track(tracker.events.editModel, { field: 'recurrence' });
    }
  };

  return (
    <div
      className={classes.root}
      id={index}
      style={{
        opacity: !verified && 0.6, transform: mounted ? 'rotate3d(1, 0, 0, 0)' : 'rotate3d(1, 0, 0, -90deg)',
        transition: !verified ? 'opacity 0.2s ease' : `transform 0.5s ease ${parseFloat(index * 0.1).toFixed(3)}s`,
        transformOrigin: '0 0 5px',
      }}
    >
      <div className={classNames(classes.slash, !verified && classes.faded)}/>

      <Avatar className={classNames(classes.avatar, !verified && classes.disabled)}>
        {payee ? payee[0].toUpperCase() : '!'}
      </Avatar>

      <div className={classes.verticalStack}>
        <div className={classes.row}>
          <PayeeSpan
            initialValue={payee}
            className={classes.payee}
            onBlur={updatePayee}
            variant="body1"
          />

          <AmountSpan
            initialValue={amount}
            negator={negator}
            className={classes.amount}
            onBlur={updateAmount}
            variant="body1"
          />
        </div>

        <div className={classes.row}>
          <CategoryField
            value={category}
            onChange={updateCategory}
            editable
            createEnabled
            variant="caption"
            margin="none"
            fontSize='0.75rem'
            disableUnderline
            className={classes.category}
            inputStyle={classes.grey}
          />

          <RecurrenceSelect
            scheduledTxn={model}
            onChange={updateRecurrence}
          />
        </div>
      </div>

      <ButtonBase
        focusRipple
        className={classes.toggleButton}
        onClick={toggleVerify}
      >
        <CheckIcon
          className={verified ? classes.greenCheck : classes.uncheck}
        />
      </ButtonBase>
    </div>
  );
}

RecurringChip.defaultProps = {
  index: 0,
  isBill: true,
};

RecurringChip.propTypes = {
  model: PropTypes.object,
  index: PropTypes.number,
  isBill: PropTypes.bool,
  reportFunc: PropTypes.func,
};
